import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import CommentCard from 'containers/cards/Comment';
import MediumCard from 'containers/cards/Medium';
import ReviewCard from 'containers/cards/Review';
import InfiniteScrollApp from '../../components/InfiniteScrollerApollo';
import ProfilePage from './Page';
import { GET_PERSONAL_FEED } from '../../schema/apolloQueries/profileQueries';

const styles = {
  card: {
    marginBottom: 10,
  },
  cards: { width: '100%' },
};

const ProfileFeedPage = ({ viewer }) => {
  const size = 20;
  const { data, fetchMore } = useQuery(GET_PERSONAL_FEED, {
    variables: {
      pageNo: 1,
      size,
      onlyMyFeeds: true,
      sort: 'MOST_RECENT',
    },
  });

  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [edges, setEdges] = useState([]);

  useEffect(() => {
    if (data?.getPersonalFeed) {
      setEdges(data?.getPersonalFeed?.feeds);
      setIsFirstLoad(false);
    }
  }, [data]);

  const onDelete = (index) => {
    setEdges((prevEdges) => prevEdges.filter((_, i) => i !== index));
  };

  const handleFetchMore = (isPage, setHasMore) => {
    return new Promise((resolve, reject) => {
      fetchMore({
        variables: {
          pageNo: isPage,
          size,
          onlyMyFeeds: true,
          sort: 'MOST_RECENT',
        },
        updateQuery: (pre, { fetchMoreResult }) => {
          if (
            pre?.getPersonalFeed.feeds?.length
            >= fetchMoreResult?.getPersonalFeed?.count
          ) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
          return pre?.getPersonalFeed.feeds?.length
            >= fetchMoreResult?.getPersonalFeed?.count
            ? pre
            : {
              getPersonalFeed: {
                __typename: pre.getPersonalFeed.__typename,
                count: pre.getPersonalFeed.count,
                feeds: [
                  ...pre.getPersonalFeed.feeds,
                  ...fetchMoreResult.getPersonalFeed.feeds,
                ],
              },
            };
        },
      })
        .then(() => {
          resolve();
        })
        .catch(() => {
          reject();
        });
    });
  };

  const setLikeOrUnlikeEdgesState = (index) => {
    setEdges((prevEdges) => {
      const newList = [...prevEdges];
      newList[index] = {
        ...newList[index],
        feedInfo: {
          ...newList[index].feedInfo,
          liked: !newList[index].feedInfo.liked,
          likeCount: !newList[index].feedInfo.liked
            ? newList[index].feedInfo.likeCount + 1
            : newList[index].feedInfo.likeCount - 1,
        },
      };
      return newList;
    });
  };

  const setCommentEdgesState = (index, feed) => {
    setEdges((prevEdges) => {
      const newList = [...prevEdges];
      newList[index] = {
        ...newList[index],
        commentInfo: { ...newList[index].commentInfo, body: feed },
      };
      return newList;
    });
  };

  const setCommentCountEdgesState = (index) => {
    setEdges((prevEdges) => {
      const newList = [...prevEdges];
      newList[index] = {
        ...newList[index],
        feedInfo: {
          ...newList[index].feedInfo,
          commentCount: newList[index].feedInfo.commentCount + 1,
        },
      };
      return newList;
    });
  };

  return (
    <ProfilePage title='Feed' viewer={viewer}>
      <InfiniteScrollApp
        handleFetchMore={handleFetchMore}
        isFirstLoad={isFirstLoad}
        isEmpty={data?.getPersonalFeed?.feeds?.length === 0}
      >
        <div style={styles.cards}>
          <AnimatePresence>
            {edges.map((comment, index) => {
              const animationProps = {
                initial: { opacity: 0, y: 20 },
                animate: { opacity: 1, y: 0 },
                exit: { opacity: 0, y: -20 },
                transition: { duration: 0.3 },
              };

              if (comment.feedType === 'Discussion') {
                return (
                  <motion.div key={comment.id} {...animationProps}>
                    <CommentCard
                      comment={comment}
                      onDelete={() => onDelete(index)}
                      onLikeOrUnlike={() => setLikeOrUnlikeEdgesState(index)}
                      onEditComment={(feed) => setCommentEdgesState(index, feed)}
                      onReplyCount={() => setCommentCountEdgesState(index)}
                      style={styles.card}
                      viewer={viewer}
                    />
                  </motion.div>
                );
              }
              if (comment.feedType === 'SocialAsset') {
                return (
                  <motion.div key={comment.id} {...animationProps}>
                    <MediumCard
                      onLikeOrUnlike={(feed) => setLikeOrUnlikeEdgesState(index, feed)}
                      onCommentCount={() => setCommentCountEdgesState(index)}
                      medium={comment}
                      style={styles.card}
                      viewer={viewer}
                    />
                  </motion.div>
                );
              }
              if (comment.feedType === 'Review') {
                return (
                  <motion.div key={comment.id} {...animationProps}>
                    <ReviewCard
                      onLikeOrUnlike={(feed) => setLikeOrUnlikeEdgesState(index, feed)}
                      onCommentCount={() => setCommentCountEdgesState(index)}
                      review={comment}
                      style={styles.card}
                      viewer={viewer}
                    />
                  </motion.div>
                );
              }
              return null;
            })}
          </AnimatePresence>
        </div>
      </InfiniteScrollApp>
    </ProfilePage>
  );
};

ProfileFeedPage.propTypes = {
  viewer: PropTypes.object.isRequired,
};

export default ProfileFeedPage;
